import type {
  InternalNode,
  NodeChange,
  NodeDimensionChange,
  NodePositionChange,
  XYPosition,
} from "@xyflow/react";
import { LATTICE, snapToGrid } from "../canvas-controls.config";

/**
 * Lattice quantization for pointer-driven node changes.
 *
 * React Flow's own `snapToGrid` is OFF for the canvas: quantizing inside
 * xyflow rounds positions before the alignment-guide competition can see
 * them, which makes sub-cell alignment stops (e.g. centers of near-equal
 * heights) unreachable. Instead the raw change stream flows into the
 * `useHelperLines` interceptor, guides compete first, and these helpers
 * apply the grid to whatever alignment didn't claim.
 *
 * The helpers reproduce xyflow's own snapping semantics:
 * - single drags round the ABSOLUTE position to the lattice;
 * - multi-drags snap the lead node and apply the same offset to the rest
 *   (mirrors xyflow's `calculateSnapOffset`), preserving relative offsets —
 *   including deliberately off-lattice aligned pairs;
 * - keyboard nudges (5px raw with the flag off) bump a full lattice step in
 *   the pressed direction, matching the old snapGrid velocity;
 * - live resizes of symmetric types round dimensions and keep the opposite
 *   edge fixed for top/left handles. Aspect-locked and hug-height types
 *   (entities, widgets, labels) resize smoothly and settle on release via
 *   their own `onResizeEnd` handlers.
 */

const round = (v: number) => snapToGrid(v, LATTICE);

/**
 * Millipixel precision cap for every coordinate the interceptor emits.
 * Guide lines are built from neighbors' REAL geometry, which can carry
 * float residue (legacy nodes store values like 1776.0000000000002);
 * without this, capturing onto such a line persists the residue — and
 * residue values defeat the Liveblocks adapter's exact-equality write
 * dedupe, turning a drag near those nodes into a storage-op stream at
 * pointer rate. Honest off-grid values (2851, 190.909) survive intact.
 */
export const cleanCoord = (v: number) => Math.round(v * 1000) / 1000;

/** Node types whose live resize steps on the lattice (symmetric settles). */
const LIVE_RESIZE_ROUND_TYPES = new Set([
  "textNote",
  "canvasShape",
  "section",
  "canvasText",
]);

type GetInternalNode = (id: string) => InternalNode | undefined;

/**
 * Offset that converts this node's parent-relative coords to absolute.
 * Derived from the node's own internals, so it is correct at any section
 * nesting depth (a parent's `.position` is itself relative and must never
 * be used as an origin).
 */
export function parentOffsetOf(internal: InternalNode): XYPosition {
  return {
    x: internal.internals.positionAbsolute.x - internal.position.x,
    y: internal.internals.positionAbsolute.y - internal.position.y,
  };
}

function quantizeAxis(
  raw: number,
  current: number,
  keyboardLike: boolean,
): number {
  const rounded = round(raw);
  // Keyboard nudges travel 5 raw px — rounding alone would collapse them to
  // a no-op. Bump one full cell in the pressed direction instead.
  if (keyboardLike && rounded === round(current) && raw !== current) {
    return rounded + Math.sign(raw - current) * LATTICE;
  }
  return rounded;
}

export function quantizeSinglePosition(
  change: NodePositionChange,
  internal: InternalNode,
  keyboardLike: boolean,
): NodePositionChange {
  if (!change.position) return change;
  const offset = parentOffsetOf(internal);
  const rawAbs = {
    x: change.position.x + offset.x,
    y: change.position.y + offset.y,
  };
  const currentAbs = internal.internals.positionAbsolute;
  return {
    ...change,
    position: {
      x: cleanCoord(
        quantizeAxis(rawAbs.x, currentAbs.x, keyboardLike) - offset.x,
      ),
      y: cleanCoord(
        quantizeAxis(rawAbs.y, currentAbs.y, keyboardLike) - offset.y,
      ),
    },
  };
}

/**
 * Multi-selection drag: snap the lead change to the lattice and shift every
 * change by the same delta (xyflow's multiDragSnapOffset semantics), so
 * relative offsets inside the selection survive — a center-aligned off-grid
 * pair stays aligned while group-dragged.
 */
export function quantizeMultiPositions(
  changes: NodeChange[],
  getInternalNode: GetInternalNode,
  keyboardLike: boolean,
): NodeChange[] {
  const lead = changes.find(
    (c): c is NodePositionChange =>
      c.type === "position" && "dragging" in c && !!c.position,
  );
  const leadInternal = lead ? getInternalNode(lead.id) : undefined;
  if (!lead?.position || !leadInternal) return changes;

  const leadOffset = parentOffsetOf(leadInternal);
  const leadRawAbs = {
    x: lead.position.x + leadOffset.x,
    y: lead.position.y + leadOffset.y,
  };
  const leadCurrentAbs = leadInternal.internals.positionAbsolute;
  const snapOffset = {
    x:
      quantizeAxis(leadRawAbs.x, leadCurrentAbs.x, keyboardLike) - leadRawAbs.x,
    y:
      quantizeAxis(leadRawAbs.y, leadCurrentAbs.y, keyboardLike) - leadRawAbs.y,
  };

  return changes.map((c) => {
    if (c.type !== "position" || !("dragging" in c) || !c.position) return c;
    // cleanCoord, not integer rounding (xyflow's own multi-drag rounds):
    // honest fractional coordinates — center alignments against media whose
    // X1 heights are fractional — must survive a group drag with relative
    // offsets preserved EXACTLY, not to ±1px.
    return {
      ...c,
      position: {
        x: cleanCoord(c.position.x + snapOffset.x),
        y: cleanCoord(c.position.y + snapOffset.y),
      },
    };
  });
}

/**
 * Live resize: for symmetric types, round dimensions to the lattice and keep
 * the opposite edge fixed when a top/left handle also moved the origin (the
 * paired position change carries no `dragging` field). Other types pass
 * through untouched — they settle in their own `onResizeEnd`.
 */
export function quantizeResizeChanges(
  changes: NodeChange[],
  getInternalNode: GetInternalNode,
): NodeChange[] {
  const dimensionsById = new Map<string, NodeDimensionChange>();
  for (const c of changes) {
    if (c.type === "dimensions" && c.dimensions) dimensionsById.set(c.id, c);
  }
  if (dimensionsById.size === 0) return changes;

  const roundedById = new Map<
    string,
    { rawW: number; rawH: number; w: number; h: number }
  >();

  const result = changes.map((c) => {
    if (c.type === "dimensions" && c.dimensions) {
      const internal = getInternalNode(c.id);
      if (!internal || !LIVE_RESIZE_ROUND_TYPES.has(internal.type ?? "")) {
        return c;
      }
      const rawW = c.dimensions.width;
      const rawH = c.dimensions.height;
      const w = round(rawW);
      const h = round(rawH);
      roundedById.set(c.id, { rawW, rawH, w, h });
      return { ...c, dimensions: { width: w, height: h } };
    }
    return c;
  });

  // Second pass: fix the origin of paired position changes so the opposite
  // edge (right/bottom) stays exactly where the raw values put it — but only
  // on the axis the handle actually moved (a pure left-handle resize must
  // not shift y, and vice versa).
  return result.map((c) => {
    if (
      c.type !== "position" ||
      "dragging" in c ||
      !c.position ||
      !roundedById.has(c.id)
    ) {
      return c;
    }
    const internal = getInternalNode(c.id);
    if (!internal) return c;
    const rounded = roundedById.get(c.id)!;
    const movedX = c.position.x !== internal.position.x;
    const movedY = c.position.y !== internal.position.y;
    return {
      ...c,
      position: {
        x: movedX ? c.position.x + (rounded.rawW - rounded.w) : c.position.x,
        y: movedY ? c.position.y + (rounded.rawH - rounded.h) : c.position.y,
      },
    };
  });
}
